'use client';

import { cloneElement, forwardRef, isValidElement, useContext, useId } from 'react';

import { css, cx } from '@/styled-system/css';

import { useIsomorphicLayoutEffect } from '../../hooks/use-isomorphic-layout-effect';
import { ModalRootContext } from './context/modal-root.context';
import { modalHeaderStyles } from './styles/modal-header.styles';
import type { ModalHeaderPropsInterface } from './types/modal-header.types';

/**
 * @since 10.0.0
 */
export const ModalHeader = forwardRef<HTMLDivElement, ModalHeaderPropsInterface>(
  (props: ModalHeaderPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;
    const { modal } = useContext(ModalRootContext);
    const id = useId();
    const styles = css(modalHeaderStyles, cssProp);

    /*
     * When the header is a single element (typically a `Heading`), label the
     * dialog from that element so `aria-labelledby` references the heading
     * itself, reusing a consumer-provided `id` when present. Otherwise, fall
     * back to labelling from the header wrapper.
     */
    const labelElement = isValidElement<{ id?: string }>(children) ? children : null;
    const labelId = labelElement?.props.id ?? id;

    useIsomorphicLayoutEffect(() => {
      modal.setLabelId(labelId);
      return () => modal.setLabelId(undefined);
    }, [labelId, modal.setLabelId]);

    return (
      <div
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Modal.Header"
        id={labelElement ? undefined : id}
        className={cx('c11n', styles, className)}
        {...rest}
      >
        {labelElement ? cloneElement(labelElement, { id: labelId }) : children}
      </div>
    );
  },
);

ModalHeader.displayName = 'Modal.Header';
